import { Controller } from "@hotwired/stimulus"

// Keeps the editor's "So wirkt diese Zulage" line in sync while the admin types.
//
// The wording lives in one place — the German i18n subtree the Ruby
// SummaryComponent reads — and is handed to this controller as the `templates`
// value, so nothing is translated here. What IS duplicated is the order the
// segments are assembled in; SummaryComponent#segments is the reference, and
// the two must be changed together.
const FORMULA_BY_TYPE = {
  stundenzulage: "rate",
  stundenlohn_multiplikator: "multiplier",
  prozent_vom_lohn: "percentage",
  tagespauschale: "fixed_per_day",
  eintragspauschale: "fixed_per_entry",
  benutzerdefiniert: "custom",
  nachtzulage: "night",
  sonntagszulage: "weekday",
  feiertagszulage: "holiday",
  verpflegungspauschale: "meal",
  kilometergeldzulage: "mileage_commute",
  unterkunft_baustelle: "mileage_lodging",
  wochentliches_fahrgeld: "mileage_commute",
  baustelle_baustelle_zulage: "mileage_site_to_site",
  entfernungspauschale: "distance_flat",
}

const DEFAULT_NIGHT_FROM = "06:00"
const DEFAULT_NIGHT_TO = "18:00"

export default class extends Controller {
  static targets = ["text"]
  static values = { templates: Object }

  connect() {
    this.render()
  }

  // Delegated from the whole editor: any input or select change re-renders.
  refresh() {
    this.render()
  }

  render() {
    if (!this.hasTextTarget || !this.templatesValue.summary) return

    const parts = [
      this.formulaSegment(),
      this.rateTierSegment(),
      ...this.frequencySegments(),
      ...this.capSegments(),
      this.minimumHoursSegment(),
      this.employeesExcludedSegment(),
      this.taxSplitSegment(),
      ...this.salaryTypeSegments(),
    ].filter(Boolean)

    this.textTarget.textContent = parts.length
      ? parts.join(this.templatesValue.separator)
      : this.templatesValue.empty
  }

  // --- segments ------------------------------------------------------------

  formulaSegment() {
    const s = this.templatesValue.summary
    switch (FORMULA_BY_TYPE[this.allowanceType]) {
      case "rate":
        return this.amountPhrase(s.per_hour, "rateInput", "amount")
      case "multiplier":
        return this.numberPhrase(s.multiplier, "multiplierInput", "factor")
      case "percentage":
        return this.numberPhrase(s.percentage, "percentageInput", "percentage")
      case "fixed_per_day":
        return this.amountPhrase(s.fixed_per_day, "fixedAmountInput", "amount")
      case "fixed_per_entry":
        return this.amountPhrase(s.fixed_per_entry, "fixedAmountInput", "amount")
      case "custom":
        return s.custom
      case "night":
        return this.interpolate(s.night_window, {
          from: this.value("referenceTimeFromInput") || DEFAULT_NIGHT_FROM,
          to: this.value("referenceTimeToInput") || DEFAULT_NIGHT_TO,
        })
      case "weekday":
        return this.weekdayPhrase()
      case "holiday":
        return this.interpolate(s.holiday, {
          source: s.holiday_sources[this.value("holidayRegionSourceInput") || "tenant"],
        })
      case "meal":
        // mealFixedAmountInput, not fixedAmountInput: every type seeds its own
        // amount field from formula_config['fixed_amount'], so the Tagespauschale
        // one is right on open and then never follows what the admin types here.
        return this.amountPhrase(s.meal, "mealFixedAmountInput", "amount") || s.meal_plain
      case "mileage_commute":
        // Kilometergeld "Strecke": Wohnort → Firmenstandort instead of → site.
        if (
          this.allowanceType === "kilometergeldzulage" &&
          this.enumValue("mileageDistanceBasisInput") === "home_to_location"
        ) {
          return s.mileage.home_to_location
        }
        return s.mileage.commute
      case "mileage_lodging":
        return s.mileage.lodging
      case "mileage_site_to_site":
        return s.mileage.site_to_site
      case "distance_flat":
        return this.distancePhrase()
      default:
        return null
    }
  }

  rateTierSegment() {
    if (!this.sectionVisible("[data-rate-tier-section]")) return null
    if (!this.checked("rateTierEnabledInput")) return null
    const hours = this.number("rateTierThresholdInput")
    if (hours === null || hours <= 0) return null

    return this.interpolate(this.templatesValue.badges.rate_tier, {
      hours: this.formatNumber(hours, 0),
      period: this.periodLabel(this.enumValue("rateTierScopeInput")),
    })
  }

  frequencySegments() {
    const badges = this.templatesValue.badges
    const raw = this.number("maxPerWeekInput")
    return [
      this.checked("oncePerDayInput") ? badges.once_per_day : null,
      this.checked("oncePerWeekInput") ? this.templatesValue.summary.once_per_week : null,
      raw ? this.interpolate(badges.max_per_week, { count: this.formatNumber(raw, 0) }) : null,
    ]
  }

  capSegments() {
    const badges = this.templatesValue.badges
    const amount = this.number("maxAmountInput")
    // The save posts max_hours as null whenever its section is hidden for the
    // selected type, so the summary must not promise a cap that will not apply.
    const hours = this.sectionVisible("[data-hours-cap-section]")
      ? this.number("maxHoursInput")
      : null
    return [
      amount
        ? this.interpolate(badges.max_amount[this.enumValue("maxAmountScopeInput") || "per_day"], {
            amount: this.formatCurrency(amount),
          })
        : null,
      hours
        ? this.interpolate(badges.max_hours, {
            hours: this.formatNumber(hours, 2),
            period: this.periodLabel(this.enumValue("maxHoursScopeInput")),
          })
        : null,
    ]
  }

  minimumHoursSegment() {
    const hours = this.number("minimumHoursInput")
    if (!hours) return null

    return this.interpolate(this.templatesValue.summary.min_hours, {
      hours: this.formatNumber(hours, 2),
    })
  }

  // Selected employees plus the archived/deactivated ones the select does not offer.
  employeesExcludedSegment() {
    const select = this.field("excludedEmployeeIdsInput")
    if (!select) return null

    const count = select.selectedOptions.length + Number(select.dataset.hiddenExcludedCount || 0)
    if (!count) return null

    const phrases = this.templatesValue.summary.employees_excluded
    return this.interpolate(count === 1 ? phrases.one : phrases.other, {
      count: this.formatNumber(count, 0),
    })
  }

  taxSplitSegment() {
    return this.checked("tenureSplitEnabledInput") ? this.templatesValue.summary.tax_split : null
  }

  salaryTypeSegments() {
    const s = this.templatesValue.summary
    return [
      this.salaryTypePhrase(s.salary_type, "salaryTypeNumberInput"),
      this.salaryTypePhrase(s.secondary_salary_type, "secondarySalaryTypeNumberInput"),
      this.salaryTypePhrase(s.tier_salary_type, "tierSalaryTypeNumberInput"),
    ]
  }

  // --- helpers -------------------------------------------------------------

  get allowanceType() {
    const select = this.element.querySelector('[data-field-editor-target="allowanceType"]')
    return select ? select.value : null
  }

  field(target) {
    return this.element.querySelector(`[data-field-editor-target="${target}"]`)
  }

  value(target) {
    const el = this.field(target)
    return el ? el.value.trim() : ""
  }

  // A scope may be a select or a segmented radio group.
  enumValue(target) {
    const els = [...this.element.querySelectorAll(`[data-field-editor-target="${target}"]`)]
    if (els.length === 1 && els[0].tagName === "SELECT") return els[0].value
    const checked = els.find((el) => el.checked)
    return checked ? checked.value : ""
  }

  checked(target) {
    const el = this.field(target)
    return Boolean(el && el.checked)
  }

  // A rule only counts while its section is offered for the selected type.
  sectionVisible(selector) {
    const section = this.element.querySelector(selector)
    return Boolean(section && !section.classList.contains("hidden"))
  }

  number(target) {
    const raw = this.value(target).replace(",", ".")
    if (raw === "") return null
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : null
  }

  amountPhrase(template, target, key) {
    const amount = this.number(target)
    if (!amount) return null
    return this.interpolate(template, { [key]: this.formatCurrency(amount) })
  }

  numberPhrase(template, target, key) {
    const value = this.number(target)
    if (!value) return null
    return this.interpolate(template, { [key]: this.formatNumber(value, 2) })
  }

  weekdayPhrase() {
    const s = this.templatesValue.summary
    const names = this.templatesValue.day_names
    const days = [...this.element.querySelectorAll('[data-field-editor-target="triggerDayInput"]')]
      .filter((el) => el.checked)
      .map((el) => names[Number(el.value)])
    return days.length
      ? this.interpolate(s.trigger_days, { days: days.join(", ") })
      : s.trigger_days_empty
  }

  distancePhrase() {
    const s = this.templatesValue.summary
    // distanceFixedAmountInput — see the meal case above for why not fixedAmountInput.
    const amount = this.number("distanceFixedAmountInput")
    const km = this.number("distanceThresholdInput")
    if (!amount || !km) return s.distance_flat_plain
    return this.interpolate(s.distance_flat, {
      amount: this.formatCurrency(amount),
      km: this.formatNumber(km, 2),
    })
  }

  salaryTypePhrase(template, target) {
    const el = this.field(target)
    if (!el || !el.value) return null
    const option = el.selectedOptions?.[0]
    // The option carries the code in data-code; a code can itself contain a hyphen,
    // so it is not parsed out of the "CODE – Bezeichnung" label.
    const code = option?.dataset?.code || option?.textContent?.split(/[–-]/)[0]?.trim()
    if (!code) return null
    return this.interpolate(template, { code })
  }

  periodLabel(scope) {
    return this.templatesValue.period_scopes_short[scope] || ""
  }

  interpolate(template, values) {
    if (!template) return null
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`%{${key}}`, value),
      template,
    )
  }

  formatNumber(value, maximumFractionDigits) {
    return new Intl.NumberFormat("de-DE", { maximumFractionDigits }).format(value)
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value)
  }
}
